import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin } from 'lucide-react';
import { cn } from '@/lib/utils';
import Eyebrow from '../Eyebrow';
import MarginNote from '../MarginNote';
import { SAY_HELLO_HREF, currentProps } from './navItems';
import { activeCodesText } from './accountLinks';

// Pieces of the redesigned site header (SiteHeader). Tokens only (no hex/rgb/hsl);
// the reference's measurements are noted where they are not obvious.

/**
 * The header's line colour. The reference draws the bar and its panels with a warm line a
 * step darker than `rule` (#bfb5a5 in its CSS, not a token): here the rule token mixed with
 * 22% muted, so the value still comes from the palette.
 */
export const HEADER_LINE = 'border-[color:color-mix(in_srgb,theme(colors.brand.rule)_78%,theme(colors.brand.muted))]';

/** A panel hanging under the bar (mobile menu, account menu): sheet, line, offset shadow. */
export const HEADER_PANEL = cn(
  'border bg-brand-sheet text-navy shadow-[3px_5px_0_theme(colors.navy.DEFAULT/8%)]',
  HEADER_LINE,
);

/** Plex Mono 12px text control (desktop nav links, "Log in", the account button). */
export const HEADER_TEXT = cn(
  'inline-block bg-transparent py-2.5 font-label text-xs font-normal leading-[1.55] text-navy',
  'underline-offset-[5px] decoration-1 hover:underline',
  'aria-[current=page]:underline aria-[current=page]:underline-offset-[6px]',
);

/**
 * The active-codes count: an orange disc with navy text (4.62:1). The number is hidden
 * from screen readers, which read "N active codes" instead.
 */
export function CountBadge({ count, className }) {
  if (!(count > 0)) return null;
  return (
    <>
      <span
        aria-hidden="true"
        className={cn(
          'inline-grid h-[1.125rem] min-w-[1.125rem] shrink-0 place-items-center rounded-full bg-brand-orange px-1',
          'font-label text-[0.625rem] font-medium leading-none text-navy',
          className,
        )}
      >
        {count}
      </span>
      <span className="sr-only">{` (${activeCodesText(count)})`}</span>
    </>
  );
}

/**
 * "Get started →": the navy block with the orange square arrow (the reference's
 * .header-action: Plex 12px, 43px tall, 27px arrow; 40px / 25px below 800px). Its own
 * element rather than the page Button: no border, no offset shadow, no lift.
 * A `<button>` (GetStartedMenu's disclosure toggle) unless `to` is given (then a router
 * link). The arrow is the same-site "→" (brief §2.1 arrow semantics: every choice stays on
 * the site); `open` turns it to point down at the open panel (instant under reduced motion).
 * Below 320px (at 16px text; wider with enlarged text) only the arrow shows and the label
 * becomes screen-reader text. If the bar is still too narrow (text enlarged some other
 * way), the label is what gives way (ellipsis), never the controls next to it.
 */
export const HeaderAction = React.forwardRef(function HeaderAction({
  to, open = false, children, className, ...rest
}, ref) {
  const Tag = to ? Link : 'button';
  return (
    <Tag
      ref={ref}
      {...(to ? { to } : { type: 'button' })}
      className={cn(
        'group inline-flex min-w-0 shrink items-center bg-navy font-label text-xs font-normal leading-none text-brand-sheet no-underline',
        'min-h-10 gap-3 px-[11px] py-[7px]',
        '[@media(max-width:22.4375em)]:gap-1.5 [@media(max-width:22.4375em)]:px-[7px] [@media(max-width:22.4375em)]:py-1.5',
        // 360–430px: a little tighter, so the whole label fits beside the deals pin
        '[@media(min-width:22.5em)_and_(max-width:26.875em)]:gap-2 [@media(min-width:22.5em)_and_(max-width:26.875em)]:px-[9px]',
        '[@media(min-width:50em)]:min-h-[2.6875rem] [@media(min-width:50em)]:gap-[1.4375rem]',
        '[@media(min-width:50em)]:px-[15px] [@media(min-width:50em)]:py-2',
        !to && 'cursor-pointer border-0 text-left',
        className,
      )}
      {...rest}
    >
      <span className="min-w-0 truncate [@media(max-width:20.4375em)]:sr-only">{children}</span>
      <span
        aria-hidden="true"
        data-header-action-arrow=""
        className={cn(
          // 25px / 27px at 16px text, in rem so the block grows with enlarged text
          'grid size-[1.5625rem] shrink-0 place-items-center bg-brand-orange font-body text-lg font-normal leading-none text-brand-sheet',
          '[@media(min-width:50em)]:size-[1.6875rem]',
          'motion-safe:transition-transform motion-safe:duration-180 motion-safe:ease-out',
          open ? 'rotate-90' : 'motion-safe:group-hover:rotate-[5deg]',
        )}
      >
        →
      </span>
    </Tag>
  );
});

/** Where "Find deals near me" goes (the guest entry point; upgrade 2026-09-28). */
export const DEALS_HREF = '/local-deals-near-me';

/**
 * "Find deals near me" beside "Get started" (owner 2026-09-28: the guest entry needs to be
 * easier to find). Phones: an orange 40px square with the map pin. From 800px: an outlined link
 * on the bar's sheet with the orange pin block, as tall as "Get started" — the pin alone to
 * 1023px, "Deals" from 1024px, the full label from 1151px (em queries, like the bar). Its
 * accessible name is always "Find deals near me" (the visible word is part of it).
 * aria-current on the page itself.
 */
export function DealsLink({ pathname }) {
  const current = typeof pathname === 'string' && pathname.toLowerCase().replace(/\/+$/, '') === DEALS_HREF;
  return (
    <Link
      to={DEALS_HREF}
      aria-label="Find deals near me"
      aria-current={current ? 'page' : undefined}
      data-header-deals=""
      className={cn(
        'group inline-flex size-10 shrink-0 items-center justify-center bg-brand-orange [@media(max-width:22.4375em)]:size-9 font-label text-xs font-normal leading-none text-navy no-underline',
        '[@media(min-width:50em)]:size-auto [@media(min-width:50em)]:min-h-[2.6875rem] [@media(min-width:50em)]:gap-2.5 [@media(min-width:50em)]:border [@media(min-width:50em)]:border-navy',
        '[@media(min-width:50em)]:bg-brand-sheet [@media(min-width:50em)]:p-2 [@media(min-width:50em)]:hover:bg-brand-paper',
        '[@media(min-width:64em)]:pr-[13px] [@media(min-width:50em)]:aria-[current=page]:bg-brand-butter',
      )}
    >
      <span
        aria-hidden="true"
        className="grid size-[1.5625rem] shrink-0 place-items-center bg-brand-orange text-brand-sheet [@media(min-width:50em)]:size-[1.6875rem]"
      >
        <MapPin className="size-4" strokeWidth={2.25} />
      </span>
      <span aria-hidden="true" className="hidden [@media(min-width:64em)]:inline [@media(min-width:71.9375em)]:hidden">Deals</span>
      <span aria-hidden="true" className="hidden [@media(min-width:71.9375em)]:inline">Find deals near me</span>
    </Link>
  );
}

/**
 * The two notes at the very top of the page, 30px in from the 1440px canvas edges, shown
 * from 1151px up (the reference hides them at 1150px and below; an em query, so with
 * enlarged text they wait for more room instead of running into the bar). The left note
 * is decoration; the right one is a real mailto link. SiteHeader leaves them out on the
 * pages in MARGIN_NOTE_HIDDEN_PATHS. A container query on the full-width layer, in the
 * notes' own em (70em: 1120px at 16px text, so never at the default size, scrollbar
 * included), hides them too when text is enlarged in a way the media query does not see
 * (page CSS, a text-only zoom): the bar then spans the width they would sit in.
 */
export function MarginNotes() {
  return (
    <div
      className="pointer-events-none absolute inset-x-0 top-0 z-40 hidden [container-type:inline-size] [@media(min-width:71.9375em)]:block"
      data-site-margin-notes=""
    >
      <div className="relative mx-auto max-w-canvas [@container(max-width:70em)]:hidden">
        <Eyebrow
          as="p"
          aria-hidden="true"
          className="absolute left-[30px] top-[35px] m-0 block text-[0.625rem] leading-[1.5] tracking-[0.02em] text-navy"
        >
          In good company.
          <br />
          Out in the neighborhood.
        </Eyebrow>
        <MarginNote
          as="a"
          href={SAY_HELLO_HREF}
          decorative={false}
          hideOnMobile={false}
          tone="inherit"
          size="sm"
          tilt={-5}
          className="pointer-events-auto absolute right-[30px] top-[35px] text-navy no-underline transition-colors duration-180 hover:text-brand-rust"
        >
          say hello
          <span aria-hidden="true">{' ↗'}</span>
          <span className="sr-only"> (email hello@tappd.app)</span>
        </MarginNote>
      </div>
    </div>
  );
}

const LINK_SIZES = {
  // desktop account menu
  sm: 'py-1.5 text-[1.0625rem] leading-snug tracking-[-0.02em]',
  // mobile menu panel, account block
  md: 'py-2 text-xl leading-[1.3] tracking-[-0.025em]',
};

/** One account row style (link or button): Manrope 500 as the reference's menu panel. */
export function accountRowClass(size = 'sm') {
  return cn(
    'flex w-full items-center gap-2.5 bg-transparent text-left font-numeral font-medium text-navy',
    'underline-offset-4 decoration-1 hover:underline aria-[current=page]:underline',
    LINK_SIZES[size] || LINK_SIZES.sm,
  );
}

/**
 * The account groups from accountGroups(), as lists separated by a rule. `onNavigate`
 * runs on every link (closes the menu). `pathname` marks the current page.
 */
export function AccountLinkList({ groups, pathname, onNavigate, size = 'sm' }) {
  return groups.map((group, i) => (
    <ul
      key={group.key}
      aria-label={group.label}
      className={cn('m-0 list-none p-0', i > 0 && 'mt-3 border-t border-brand-rule pt-3')}
    >
      {group.links.map((link) => (
        <li key={link.key}>
          <Link to={link.to} onClick={onNavigate} className={accountRowClass(size)} {...currentProps(pathname, link.to)}>
            <span>{link.label}</span>
            <CountBadge count={link.badge} />
          </Link>
        </li>
      ))}
    </ul>
  ));
}
